import React, { useEffect, useState, ChangeEvent, useRef, useMemo } from "react";
import Chip from "@mui/material/Chip";
import {
    TextField, Grid, MenuItem, Select, InputLabel, FormControl,
    Button, InputAdornment, IconButton, Typography, Box, Stack,
    Modal, Divider, Switch, CircularProgress, TextareaAutosize,
    Paper, FormControlLabel, Checkbox, LinearProgress, RadioGroup,
    Radio, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Button as MuiButton
} from "@mui/material";
import MicIcon           from "@mui/icons-material/Mic";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import Ocesha_header     from "pages/ocesha_landing/ocesha_header";
import { styled }        from "@mui/material/styles";
import Table             from "@mui/material/Table";
import TableBody         from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer    from "@mui/material/TableContainer";
import TableHead         from "@mui/material/TableHead";
import TableRow          from "@mui/material/TableRow";
import { isMobile }      from "react-device-detect";
import { useCreateCampaignMutation }   from "../../../redux/api/promptApi";
import { toast }                       from "react-toastify";
import { useLazyCreateCheckoutSessionQuery } from "../../../redux/api/subscriptionApi";
import PriorityHighIcon  from "@mui/icons-material/PriorityHigh";
import { usePlanDetailsMutation, useCampaignDetailsMutation } from "../../../redux/api/productApi";
import { useGetUserDetailsMutation }   from "../../../redux/api/authApi";
import { useLocation, useNavigate }    from "react-router-dom";
import {
    useGetpagefrocampaignMutation, useGetCnameEntriesMutation,
    useUpdateAutoPublishFlagMutation, useGetverifyscriptMutation,
    useCheckverifiedMutation
} from "../../../redux/api/promptApi";
import TaskAltIcon       from "@mui/icons-material/TaskAlt";
import CloseIcon         from "@mui/icons-material/Close";
import { IPlanDetail }   from "../../../redux/api/types";
import { useAppSelector } from "../../../redux/store";
import axios             from "axios";
import { useCampaignListMutation }     from "../../../redux/api/promptApi";
import InfoIcon          from "@mui/icons-material/Info";
import ProgressBar       from "@ramonak/react-progress-bar";
import { getCookie, removeCookie } from "../../../utils/storageUtils";
import { useAppDispatch }       from "../../../redux/store";
import { logout }               from "redux/features/userSlice";
import MicNoneOutlinedIcon  from "@mui/icons-material/MicNoneOutlined";
import SendIcon             from "@mui/icons-material/Send";
import RohanImg             from "../../../assets/podcast_monster/rohan_png.png";
import VolumeUpIcon         from "@mui/icons-material/VolumeUp";
import VolumeOffIcon        from "@mui/icons-material/VolumeOff";
import { useTTSQueue }      from "hooks/useTTSQueue";
import ArrowBackIcon        from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon     from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon      from "@mui/icons-material/AutoAwesome";
import CheckIcon            from "@mui/icons-material/Check";

// ── New design icons ──────────────────────────────────────────────────────────
import GraphicEqIcon            from "@mui/icons-material/GraphicEq";
import DescriptionOutlinedIcon  from "@mui/icons-material/DescriptionOutlined";
import LanguageIcon             from "@mui/icons-material/Language";
import YouTubeIcon              from "@mui/icons-material/YouTube";
import AccessTimeIcon           from "@mui/icons-material/AccessTime";
import MicRoundedIcon           from "@mui/icons-material/MicRounded";
import PlayCircleOutlinedIcon   from "@mui/icons-material/PlayCircleOutlined";
import {
    useStartInterviewTriggerMutation,
    useLazyGetTaskStatusQuery as useLazyDTTaskStatusQuery,
} from "../../../redux/api/digitalTwinApi";
import { saveVoiceSample } from "utils/digitalTwinVoice";
import { getGreetingUrl, localizedClipUrl } from "../../../utils/voiceClips";
import { getContentLanguage } from "../../../utils/contentLanguage";
import { redirectAfterLogout } from "../../../utils/logoutRedirect";
// intro clip in the site language (falls back to the English WAV)
const localizedIntro = { current: "" };

// ─── Existing types (unchanged) ───────────────────────────────────────────────

type MediaItem = { path: string; name: string; duration: string; progress: number; status: string; };
type ProcessingItem = { identifier: string; name: string; progress: number; status: "pending" | "in_progress" | "complete" | "failed"; };
type ContentProcessingStatus = { progress: number; status: "pending" | "in_progress" | "complete"; items: ProcessingItem[]; };
type ProcessingStatus = {
    overall_progress: number;
    overall_status: "pending" | "in_progress" | "complete";
    content: {
        audio: ContentProcessingStatus;
        video: ContentProcessingStatus;
        youtube_urls: ContentProcessingStatus;
        web_urls: ContentProcessingStatus;
        documents: ContentProcessingStatus;
        text_documents: ContentProcessingStatus;
    };
};
type ContentType = "audio" | "video" | "youtube_urls" | "web_urls";
type UpdateProcessingStatusParams = {
    courseId: string;
    contentType: ContentType;
    index: number;
    status: ProcessingItem["status"];
    progress: number;
    transcription?: string;
    identifier?: string;          
};
type TranscribeMediaParams = { courseId: string; media_file_path: string; original_filename: string; contentType: ContentType; };
type ProcessMediaListParams = { courseId: string; mediaList: MediaItem[]; contentType: ContentType; };
type CourseMedia = { _id: string; audio?: { identifier: string }[]; video?: { identifier: string }[]; };

// ─── New-design sub-components ────────────────────────────────────────────────

/** Maps backend content-type keys to display config */
const CONTENT_TYPE_CONFIG: Record<string, { label: string; description: string; icon: React.ReactNode; iconBg: string }> = {
    audio: {
        label:       "Analyzing Audio Content",
        description: "Transcribing conversations, identifying topics, and understanding how you communicate.",
        icon:        <GraphicEqIcon sx={{ color: "#c084fc", fontSize: 22 }} />,
        iconBg:      "linear-gradient(135deg,rgba(139,92,246,0.4),rgba(109,40,217,0.6))",
    },
    video: {
        label:       "Analyzing Video Content",
        description: "Transcribing your video, identifying topics, and understanding how you present ideas.",
        icon:        <PlayCircleOutlinedIcon sx={{ color: "#c084fc", fontSize: 22 }} />,
        iconBg:      "linear-gradient(135deg,rgba(139,92,246,0.4),rgba(109,40,217,0.6))",
    },
    documents: {
        label:       "Reviewing Documents",
        description: "Extracting key insights, frameworks, and expertise from your uploaded materials.",
        icon:        <DescriptionOutlinedIcon sx={{ color: "#4ade80", fontSize: 22 }} />,
        iconBg:      "linear-gradient(135deg,rgba(34,197,94,0.4),rgba(21,128,61,0.6))",
    },
    text_documents: {
        label:       "Processing Text Content",
        description: "Analyzing your written text to extract key themes, insights, and expertise.",
        icon:        <DescriptionOutlinedIcon sx={{ color: "#60a5fa", fontSize: 22 }} />,
        iconBg:      "linear-gradient(135deg,rgba(59,130,246,0.4),rgba(29,78,216,0.6))",
    },
    web_urls: {
        label:       "Scanning Websites & URLs",
        description: "Analyzing linked content to better understand your background, services, and authority.",
        icon:        <LanguageIcon sx={{ color: "#fb923c", fontSize: 22 }} />,
        iconBg:      "linear-gradient(135deg,rgba(251,146,60,0.4),rgba(194,65,12,0.6))",
    },
    youtube_urls: {
        label:       "Processing YouTube Videos",
        description: "Extracting insights and transcriptions from your YouTube content.",
        icon:        <YouTubeIcon sx={{ color: "#f87171", fontSize: 22 }} />,
        iconBg:      "linear-gradient(135deg,rgba(239,68,68,0.4),rgba(185,28,28,0.6))",
    },
};

type TaskStatus = "complete" | "in_progress" | "pending" | "failed";

const StatusBadge = ({ status }: { status: TaskStatus }) => {
    const map: Record<TaskStatus, { label: string; bg: string; color: string }> = {
        complete:    { label: "Complete",    bg: "rgba(34,197,94,0.15)",   color: "#22c55e" },
        in_progress: { label: "In progress", bg: "rgba(75,165,235,0.15)",  color: "#4BA5EB" },
        pending:     { label: "Pending",     bg: "rgba(255,255,255,0.08)", color: "#9ca3af" },
        failed:      { label: "Failed",      bg: "rgba(239,68,68,0.15)",   color: "#ef4444" },
    };
    const cfg = map[status] ?? map.pending;
    return (
        <Box sx={{
            px: 1.5, py: 0.4, borderRadius: 99,
            background: cfg.bg, color: cfg.color,
            fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
        }}>
            {cfg.label}
        </Box>
    );
};

export const retryAsync = async <T,>(
  fn: () => Promise<T>,
  retries = 2,
  delay = 1500,
  validate?: (result: T) => boolean
): Promise<T> => {
  try {
    const result = await fn();

    // custom validation (like empty courses)
    if (validate && !validate(result)) {
      if (retries <= 0) throw new Error("Validation failed");

      console.log("Retrying due to empty/invalid response...");
      await new Promise((res) => setTimeout(res, delay));

      return retryAsync(fn, retries - 1, delay, validate);
    }

    return result;
  } catch (err) {
    if (retries <= 0) throw err;

    console.log(`Retrying... attempts left: ${retries}`);
    await new Promise((res) => setTimeout(res, delay));

    return retryAsync(fn, retries - 1, delay, validate);
  }
};

interface TaskCardProps {
    type:     string;
    progress: number;
    status:   TaskStatus;
    items:    ProcessingItem[];
}
const TaskCard: React.FC<TaskCardProps> = ({ type, progress, status, items }) => {
    const cfg = CONTENT_TYPE_CONFIG[type] ?? {
        label:       type.replace(/_/g, " "),
        description: "Processing your content.",
        icon:        <DescriptionOutlinedIcon sx={{ color: "#9ca3af", fontSize: 22 }} />,
        iconBg:      "rgba(255,255,255,0.08)",
    };

    return (
        <Box sx={{
            background:   "rgba(255,255,255,0.025)",
            border:       "1px solid rgba(255,255,255,0.07)",
            borderRadius: "14px",
            paddingTop:  "20px",
            paddingBottom: "20px",
            paddingRight: "16px",
            paddingLeft: "16px"
        }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
                {/* Icon */}
                <Box sx={{
                    width: 48, height: 48, borderRadius: "12px",
                    background: cfg.iconBg, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    {cfg.icon}
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.6}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#e8eaf0" }}>
                            {cfg.label}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, ml: 1 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#e8eaf0" }}>
                                {progress}%
                            </Typography>
                            <StatusBadge status={status} />
                        </Stack>
                    </Stack>

                    <Typography sx={{ fontSize: 13, color: "#7a7d96", lineHeight: 1.55, mb: 1.2 }}>
                        {cfg.description}
                    </Typography>

                    {/* Progress bar */}
                    <Box sx={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                        <Box sx={{
                            height: "100%", borderRadius: 99,
                            width: `${progress}%`,
                            background: status === "complete"
                                ? "linear-gradient(90deg,#22c55e,#4ade80)"
                                : status === "failed"
                                    ? "#ef4444"
                                    : "linear-gradient(90deg,#0168B5,#4BA5EB)",
                            transition: "width 0.8s ease",
                        }} />
                    </Box>

                    {/* Individual file names (collapsed) */}
                    {items.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {items.map((item, i) => (
                                <Stack key={i} direction="row" alignItems="center" spacing={1} mt={0.5}>
                                    <Box sx={{
                                        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                                        background: item.status === "complete" ? "#22c55e"
                                            : item.status === "failed" ? "#ef4444"
                                                : item.status === "in_progress" ? "#4BA5EB"
                                                    : "rgba(255,255,255,0.25)",
                                    }} />
                                    <Typography sx={{ fontSize: 12, color: "#7a7d96" }}>
                                        {item.name} — {item.progress}%
                                    </Typography>
                                </Stack>
                            ))}
                        </Box>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const New_Course_2 = () => {

    const navigate  = useNavigate();
    const location  = useLocation();
    const dispatch  = useAppDispatch();

    const { FromEdit }               = location.state || {};
    const { campIdFrEdit }           = location.state || {};
    const { subscriptionFromCampList } = location.state || {};
    const { mode }                   = location.state || {};
    const FromSignUp                 = location.state?.FromSignUp || "";
    const FromDT                     = location.state?.FromDT    || "";

    // ── All existing state (unchanged) ────────────────────────────────────────
    const [sourceUrl, setSourceUrl]             = useState<string>("");
    const [blogcount, setBlogCount]             = useState<any>(1);
    const [sourceYoutubeUrl, setSourceYoutubeUrl] = useState<any>("");
    const [sourceText, setSourceText]           = useState<string>("");
    const [videoTag, setVideoTag]               = useState<string>("");
    const [twitterUrl, settwitterUrl]           = useState<string>("");
    const [fbUrl, setfbUrl]                     = useState<string>("");
    const [pintrestUrl, setPintrestUrl]         = useState<string>("");
    const [linkedinUrl, setLinkedinUrl]         = useState<string>("");
    const [ctaLabel, setCtaLabel]               = useState<string>("");
    const [ctaLink, setCtaLink]                 = useState<string>("");
    const [isRecording, setIsRecording]         = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef   = useRef<BlobPart[]>([]);
    const [openConfirm, setOpenConfirm]         = useState(false);
    const [questionText, setQuestionText]       = useState("");
    const [chatMessages, setChatMessages]       = useState<any[]>([]);
    const [isChatLoading, setIsChatLoading]     = useState(false);
    const [lastBotText, setLastBotText]         = useState<string>("");
    const prevTextRef   = useRef("");
    const chatScrollRef = useRef<HTMLDivElement | null>(null);
    const hasFetched    = useRef(false);
    const [isListening, setIsListening]         = useState(false);
    const textareaRef   = useRef<HTMLTextAreaElement | null>(null);
    const recognitionRef = useRef<any>(null);
    const [campaignName, setCampaignName]       = useState<string>("");
    const [urlPrefix, setUrlPrefix]             = useState<string>("https://");
    const [planDetails, setPlanDetails]         = useState<any>();
    const [campDetails, setCampDetails]         = useState<any>();
    const [campaignId, setCampaignId]           = useState<any>();
    const [userDetails, setUserDetails]         = useState<any>();
    const [openpaymentInstructModal, setOpenPaymentInstructModal] = useState<boolean>(false);
    const [autoPublish, setAutopublish]         = useState<any>(false);
    const [limitPublish, setLimitPublish]       = useState<any>(true);
    const [isloadingGetPage, setIsLoadingGetPage] = useState<boolean>(false);
    const [getPageError, setGetPageError]       = useState(false);
    const [loaderProgress, setLoaderProgress]   = useState<any>(4);
    const [upgradeModal, setUpgradeModal]       = useState<boolean>(false);
    const [loading, setLoading]                 = useState(false);
    const [loadingText, setLoadingText]         = useState<any>("");
    const [intervaltime, setIntervalTime]       = useState(4000);
    const [helperPopup, setHelperPopup]         = useState<boolean>(false);
    const [helpervideosrc, setHelperVideoSrc]   = useState<any>();
    const [isScrapcheckloading, setIsScrapcheckloading] = useState<boolean>(false);
    const [activeItem, setActiveItem]           = useState(localStorage.getItem("activeItem") || "dashboard");
    const [topics, setTopics]                   = useState<any>();
    const [topicsList, setTopicsList]           = useState<boolean>(false);
    const [selectedTopic, setSelectedTopic]     = useState<any>();
    const [keywords, setKeywords]               = useState<any>();
    const [keywordsList, setKeywordsList]       = useState<boolean>(false);
    const [selectedKeyword, setSelectedKeyword] = useState<any>();
    const [isKeywordLoader, setIsKeywordLoader] = useState<boolean>(false);
    const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
    const [isProcessing, setIsProcessing]       = useState(false);
    const [courseMedia, setCourseMedia]         = useState<CourseMedia | null>(null);
    const [transcribedTxt, setTransccribedTxt]  = useState<any>();
    const [scrappedContent, setScrappedContent] = useState<any>();
    const [isUpdateLoader, setIsUpdateLoader]   = useState<boolean>(false);
    const [campaignDatas, setCampaignDatas]     = useState<any>();
    const [uploadedvideopath, setUploadedvideopath] = useState("");
    const [userBusinessText, setUserBusinessText] = useState("");
    const [selectedCourses, setSelectedCourses] = useState<string[]>([]);

    const user_email = localStorage.getItem("user_email") || getCookie("user_email") || "";
    const apiUrl     = process.env.REACT_APP_SERVER_ENDPOINT || "";
    const courseId   = localStorage.getItem("course_id") || "";

    // ── Redux hooks (unchanged) ───────────────────────────────────────────────
    const [createCampaignMutation]         = useCreateCampaignMutation();
    const [createCheckoutSession]          = useLazyCreateCheckoutSessionQuery();
    const [getUserDetailsMutation]         = useGetUserDetailsMutation();
    const [getuserPlanDetails]             = usePlanDetailsMutation();
    const [getuserCampDetails]             = useCampaignDetailsMutation();
    const [getPageforCampaign]             = useGetpagefrocampaignMutation();
    const [getCnameEntriesMutation]        = useGetCnameEntriesMutation();
    const [getverifyscript]                = useGetverifyscriptMutation();
    const [getcheckverified]               = useCheckverifiedMutation();
    const [updateAutoPublishCheckMutation] = useUpdateAutoPublishFlagMutation();
    const [campaignListMutation]           = useCampaignListMutation();
    const [documentProgress, setDocumentProgress] = useState(0);
    const [textProgress, setTextProgress]         = useState(0);

    const [currentAudioType, setCurrentAudioType] = useState<"intro" | "question" | null>(null);

    const { playText, stop, stopAll, audioRef, isMuted, setIsMuted, isPlaying } = useTTSQueue();

    // ── AI Interview (disabled — commented out for possible reuse later) ──────
    // const [isPreparingInterview, setIsPreparingInterview] = useState(false);
    // const [interviewReady, setInterviewReady]             = useState(false);
    // const [interviewError, setInterviewError]             = useState(false);
    // const interviewStartedRef                             = useRef(false);

    // const [startInterviewTrigger] = useStartInterviewTriggerMutation();
    // const [getDTTaskStatus]       = useLazyDTTaskStatusQuery();

    // // Poll the digital-twin task until done
    // const pollInterviewTask = async (taskId: string) => {
    //     while (true) {
    //         const res = await getDTTaskStatus(taskId).unwrap();
    //         if (res.state === "done") return res.result;
    //         if (res.state === "error" || res.state === "failed") {
    //             throw new Error("Interview task failed");
    //         }
    //         await new Promise(r => setTimeout(r, 5000));
    //     }
    // };
    // ────────────────────────────────────────────────────────────────────────

    

    useEffect(() => {
        const hasDocuments = (courseMedia as any)?.result?.documents?.length > 0;
        if (!hasDocuments) return;

        const interval = setInterval(() => {
            setDocumentProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 10;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [courseMedia]);

    // Dummy progress for Text content
    useEffect(() => {
        const textContent = (courseMedia as any)?.result?.text_documents;
        const hasText = Array.isArray(textContent)
            ? textContent.length > 0
            : !!textContent;
        if (!hasText) return;

        const interval = setInterval(() => {
            setTextProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 10;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [courseMedia]);

    // ── TTS mute sync (unchanged) ─────────────────────────────────────────────
    useEffect(() => {
        (window as any).isMutedGlobal = isMuted;
        if (audioRef.current) audioRef.current.muted = isMuted;
    }, [isMuted]);

    useEffect(() => {
        if (!lastBotText || prevTextRef.current === lastBotText) return;
        prevTextRef.current = lastBotText;
        stop(); setCurrentAudioType(null); playText(lastBotText);
    }, [lastBotText]);

    // ── Processing helpers (unchanged) ────────────────────────────────────────
    const getSectionProgress = (items: ProcessingItem[]) => {
        if (!items.length) return 0;
        return Math.round(items.reduce((sum, i) => sum + i.progress, 0) / items.length);
    };
    const getSectionStatus = (items: ProcessingItem[]): TaskStatus => {
        if (items.some(i => i.status === "failed"))    return "failed";
        if (items.every(i => i.status === "complete")) return "complete";
        if (items.some(i => i.status === "in_progress")) return "in_progress";
        return "pending";
    };

    const getProcessingStatus = async (courseId: string) => {
        const res = await axios.post(`${apiUrl}/api/prompts/get_course_processing_status`, { course_id: courseId });
        return res.data;
    };

    // ── All async functions (unchanged) ───────────────────────────────────────
    async function updateProcessingStatus({ courseId, contentType, index, status, progress, transcription, identifier }: UpdateProcessingStatusParams) {
        await fetch(`${apiUrl}/api/prompts/update_processing_status`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ course_id: courseId, content_type: contentType, index, status, progress, transcription, identifier }),
        });
    }

    async function scrapeWebsite(url: string): Promise<string> {
        const res = await fetch(`${apiUrl}/api/prompts/scrap_page_test`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ start_url: url.startsWith("http") ? url : `https://${url}`, max_pages: 20, concurrency: 5, follow_external: false, save_to_file: false }),
        });
        if (!res.ok) throw new Error("Scraping failed");
        const data = await res.json();
        setScrappedContent(data.summary);
        return data.summary;
    }

    async function processWebUrls(courseId: string, webUrls: any[]) {
        for (let i = 0; i < webUrls.length; i++) {
            const item = webUrls[i];
            if (item.status === "complete") continue;
            try {
                await updateProcessingStatus({ courseId, contentType: "web_urls", index: i, status: "in_progress", progress: 25, identifier: item.url });
                const summary = await scrapeWebsite(item.url);
                await updateProcessingStatus({ courseId, contentType: "web_urls", index: i, status: "complete", progress: 100, transcription: summary,  identifier: item.url });
            } catch (err) {
                console.error(err);
                await updateProcessingStatus({ courseId, contentType: "web_urls", index: i, status: "failed", progress: 0, identifier: item.url });
            }
        }
    }

    async function transcribeMedia({ courseId, media_file_path, original_filename, contentType }: TranscribeMediaParams) {
        const formData = new FormData();
        formData.append("course_id", courseId); formData.append("media_file_path", media_file_path);
        formData.append("original_filename", original_filename); formData.append("content_type", contentType);
        const res  = await fetch(`${apiUrl}/api/prompts/get_video_audio_transcription`, { method: "POST", body: formData });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || data?.message || "Transcription failed");
        setTransccribedTxt(data?.data);
        return data;
    }

    async function processMediaList({ courseId, mediaList, contentType }: ProcessMediaListParams) {
        for (let i = 0; i < mediaList.length; i++) {
            const media = mediaList[i];
            if (media.status === "complete") continue;
            try {
                await updateProcessingStatus({ courseId, contentType, index: i, status: "in_progress", progress: 10, identifier: media.path });
                const response = await transcribeMedia({ courseId, media_file_path: media.path, original_filename: media.name, contentType });
                await updateProcessingStatus({ courseId, contentType, index: i, status: "complete", progress: 100, transcription: response.data, identifier: media.path });
            } catch (err) {
                console.error(err);
                await updateProcessingStatus({ courseId, contentType, index: i, status: "failed", progress: 0, identifier: media.path });
            }
        }
    }

    async function processAllMedia(courseMedia: any) {
        const course = courseMedia.result;
        if (course.audio?.length)    await processMediaList({ courseId: course._id, mediaList: course.audio, contentType: "audio" });
        if (course.video?.length)    await processMediaList({ courseId: course._id, mediaList: course.video, contentType: "video" });
        if (course.web_urls?.length) await processWebUrls(course._id, course.web_urls);
    }

    const fetchCourseMedia = async () => {
        const res = await axios.post(`${apiUrl}/api/prompts/get_course_media`, { course_id: courseId });
        setCourseMedia(res.data);
    };

     const updateCombinedText = async (courseId: string) => {
        const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

        try {
        const res = await axios.post(
            `${apiUrl}/api/prompts/update_combined_text?course_id=${courseId}`,
            {},
            {
            headers: { accept: "application/json" },
            }
        );

        return res.data;
        } catch (err) {
        console.error("update_combined_text failed", err);
        throw err;
        }
    };

    // ── Polling (unchanged) ───────────────────────────────────────────────────
    useEffect(() => {
        if (!courseId) return;
        const fetchStatus = async () => {
            const data = await getProcessingStatus(courseId);
            setProcessingStatus(data);
        };
        fetchStatus();
        const interval = setInterval(fetchStatus, 5000);
        return () => clearInterval(interval);
    }, [courseId]);

    useEffect(() => {
        if (!courseId) return;
        fetchCourseMedia();
    }, [courseId]);

    useEffect(() => {
        if (!courseMedia || isProcessing) return;
        const runProcessing = async () => {
            setIsProcessing(true);
            try { await processAllMedia(courseMedia); }
            finally { setIsProcessing(false); }
        };
        runProcessing();
    }, [courseMedia]);

    // ── overallStatus (unchanged logic) ──────────────────────────────────────
    const overallStatus = useMemo(() => {
        if (!processingStatus?.content) return "pending";

        const allItems = Object.values(processingStatus.content)
            .flatMap((section: any) =>
                Array.isArray(section)
                    ? section
                    : (section?.items ?? [])
            );

        // Backend items
        const backendComplete =
            allItems.length === 0 ||
            allItems.every((i: any) => i.status === "complete");

        // Documents
        const hasDocuments =
            (courseMedia as any)?.result?.documents?.length > 0;

        const documentsComplete =
            !hasDocuments || documentProgress >= 100;

        // Text
        const textContent =
            (courseMedia as any)?.result?.text_documents;

        const hasText =
            Array.isArray(textContent)
                ? textContent.length > 0
                : !!textContent;

        const textComplete =
            !hasText || textProgress >= 100;

        // FINAL COMPLETE
        if (
            backendComplete &&
            documentsComplete &&
            textComplete
        ) {
            return "complete";
        }

        // FAILED
        if (allItems.some((i: any) => i.status === "failed")) {
            return "failed";
        }

        // IN PROGRESS
        const docsInProgress =
            hasDocuments &&
            documentProgress > 0 &&
            documentProgress < 100;

        const textInProgress =
            hasText &&
            textProgress > 0 &&
            textProgress < 100;

        if (
            allItems.some((i: any) => i.status === "in_progress") ||
            docsInProgress ||
            textInProgress
        ) {
            return "in_progress";
        }

        return "pending";
    }, [
        processingStatus,
        courseMedia,
        documentProgress,
        textProgress,
    ]);

    // Fire startInterviewTrigger once media analysis is complete.
    // Persist the first question + session to localStorage so the next page
    // (New_Course) can pick it up via its existing resume logic.
    useEffect(() => {
        if (overallStatus !== "complete") return;

        (async () => { 
                await retryAsync(
                () => updateCombinedText(courseId),
                2,
                1500
            );
        })();

        // ── AI Interview (disabled — commented out for possible reuse later) ──
        // if (interviewStartedRef.current) return;

        // if (interviewReady) return;

        // interviewStartedRef.current = true;

        // (async () => {
        //     setIsPreparingInterview(true);
        //     setInterviewError(false);
        //     try {
        //         const mergedContent = `${transcribedTxt || ""}\n\n${scrappedContent || ""}`.trim();

        //         const triggerRes = await startInterviewTrigger({
        //             user_id: user_email,
        //             language: "English",
        //             combined_text: mergedContent,
        //         }).unwrap();

        //         const result = await pollInterviewTask(triggerRes.task_id);

        //         // Mirror New_Course's persistSession() exactly so the next page
        //         // initializes its useState from localStorage with this data.
        //         localStorage.setItem("dt_session_id",       result.session_id);
        //         localStorage.setItem("dt_current_question", result.question);
        //         localStorage.setItem("dt_current_stage",    result.stage);
        //         localStorage.setItem("dt_session_history",  JSON.stringify([]));
        //         if (result.audio_path) {
        //             localStorage.setItem("dt_current_audio_path", result.audio_path);
        //         }

        //         console.log("combined text updated");

        //         setInterviewReady(true);
        //     } catch (err) {
        //         console.error("Failed to prepare interview:", err);
        //         setInterviewError(true);
        //         interviewStartedRef.current = false; // allow retry if status changes again
        //     } finally {
        //         setIsPreparingInterview(false);
        //     }
        // })();
        // ────────────────────────────────────────────────────────────────────
    }, [overallStatus, transcribedTxt, scrappedContent, user_email]);

    // Was: `overallStatus === "complete" && interviewReady` — the interview
    // gate is commented out above, so continuing now only depends on
    // overallStatus. Restore the `&& interviewReady` clause when the
    // AI Interview section is re-enabled.
    const canContinue = overallStatus === "complete";

    // ── User / plan data (unchanged) ──────────────────────────────────────────
    const fetchData = async () => {
        try {
            const response = await getUserDetailsMutation({ email: user_email || getCookie("user_email") || "", filter: "", start_date: "", end_date: "" });
            if ("data" in response) { setUserDetails(response.data.data); }
        } catch (error) { console.log(error); }
    };
    useEffect(() => { fetchData(); }, []);

    const fetchPlanDatas = async () => {
        try {
            const response = await getuserPlanDetails({ email: user_email || getCookie("user_email") || "", campaignid: "" });
            if ("data" in response) setPlanDetails(response.data.data);
        } catch (error) { console.log(error); }
    };
    useEffect(() => { fetchPlanDatas(); }, []);

    // ── Audio / TTS (unchanged) ────────────────────────────────────────────────
    const stopAllAudio = () => {
        stopAll();
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };

    const location_audio = useLocation();

    useEffect(() => {
    
        stopAllAudio();

    }, [location_audio.pathname]);

    useEffect(() => {
        // On every page load, nuke any audio leaking from a previous route
        stopAll();
        document.querySelectorAll("audio").forEach(a => {
          try { a.pause(); a.currentTime = 0; a.src = ""; } catch {}
        });
        return () => {
          stopAll();   // and again on the way out
        };
      }, []);


    useEffect(() => { stopAllAudio(); }, [location_audio.pathname]);

    const secondAudio = "https://tts.ocesha.ai/media/48845f03-61a7-4c5e-890f-7abb5a2e1a84.wav";

    useEffect(() => {
        stopAllAudio();
        if (!userDetails?.first_name || hasFetched.current) return;
        hasFetched.current = true;
        fetchAudio();
    }, [userDetails?.first_name]);

    useEffect(() => {
        const el = chatScrollRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        });
    }, [chatMessages, isChatLoading]);

    const fetchAudio = async () => {
        try {
            // Site language other than English: "Hi <name>" and the intro clip in that
            // language (WAVs generated once per language and reused, see utils/voiceClips.ts)
            localizedClipUrl(secondAudio).then((u) => { localizedIntro.current = u; }).catch(() => {});
            const localizedGreeting = await getGreetingUrl(userDetails?.first_name || "").catch(() => null);
            if (localizedGreeting) { playAudio(localizedGreeting); return; }


            const params = new URLSearchParams({
                text: `Hi ${userDetails?.first_name}`,
                voice: "default",       // required — must be sent
                exaggeration: "0.5",           // optional, but explicit is clearer
                cfg_weight: "0.2",
            });

            const res  = await axios.post(`${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak?${params.toString()}`);
            const data = res?.data;
            if (data?.audio_path) playAudio(data.audio_path);
        } catch (err) { console.log("Error fetching audio:", err); }
    };

    const playAudio = (url: string) => {
        const audio = new Audio(url);
        audioRef.current = audio; audio.muted = isMuted;
        setCurrentAudioType("intro");
        audio.play().catch(() => {
            const retryPlay = () => { audio.play().catch(() => {}); document.removeEventListener("click", retryPlay); document.removeEventListener("keydown", retryPlay); };
            document.addEventListener("click", retryPlay, { once: true });
            document.addEventListener("keydown", retryPlay, { once: true });
        });
        audio.onended = () => {
            const nextAudio = new Audio(localizedIntro.current || secondAudio);
            audioRef.current = nextAudio; nextAudio.muted = isMuted;
            setCurrentAudioType("question");
            nextAudio.play().catch(() => {}); nextAudio.onended = () => {
                setCurrentAudioType(null);
            };
        };
    };

    const toggleMute = () => {
        setIsMuted(prev => {
            const next = !prev;
            if (audioRef.current) { if (next) audioRef.current.pause(); else audioRef.current.play().catch(() => {}); }
            return next;
        });
    };

    // ── Recording (unchanged) ─────────────────────────────────────────────────
    const startRecording = async () => {
        if (!navigator.mediaDevices?.getUserMedia) return;
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
        audioChunksRef.current = [];
        mediaRecorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        mediaRecorder.onstop = sendAudioToBackend;
        mediaRecorder.start(); mediaRecorderRef.current = mediaRecorder; setIsRecording(true);
    };
    const stopRecording = () => { mediaRecorderRef.current?.stop(); setIsRecording(false); };

    const sendAudioToBackend = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/webm",
        });

        await saveVoiceSample(audioBlob);

        const formData = new FormData();
        formData.append("audio", audioBlob, "speech.webm");
        // language of the speech (Japanese, Spanish …) so it is transcribed correctly
        formData.append("language", getContentLanguage().split("-")[0]);
        try {
            const res  = await fetch(`${process.env.REACT_APP_SPEECH_TO_TEXT_SERVICE}/speech_to_text`, { method: "POST", body: formData });
            const data = await res.json();
            setQuestionText(prev => `${prev} ${data.text}`.trim());
        } catch (err) { console.error("Speech upload failed", err); }
    };

    // ── Chat (unchanged) ──────────────────────────────────────────────────────
    const stripHtml = (html: string) => { const d = document.createElement("div"); d.innerHTML = html; return d.textContent || d.innerText || ""; };

    const formatAIResponse = (text: string) => {
        if (!text) return "";
        return text
            .replace(/\*{2,}/g, "").replace(/#{1,}/g, "").replace(/^\* (.+)$/gm, "• $1")
            .replace(/(?:^|\n)(• .*(?:\n• .*)*)/g, match =>
                `<ul>${match.trim().split("\n").map(l => `<li>${l.replace(/^• /, "")}</li>`).join("")}</ul>`)
            .replace(/^(.+):$/gm, "<h3>$1</h3>")
            .replace(/^(?!<h3>|<ul>|<li>|<\/ul>)(.+)$/gm, "<p>$1</p>")
            .replace(/<p>\s*<\/p>/g, "");
    };

    const getChatBotResponse = async (query: string) => {
        if (!query.trim()) return;
        setChatMessages(prev => [...prev, { role: "user", text: query }]);
        setQuestionText(""); setIsChatLoading(true);
        try {
            const res  = await axios.post(`${apiUrl}/api/prompts/chat_answer`, { courseid: "6a1498dc195a2644a8352673", query, chapter: "" });
            let answer = (res?.data?.response || "No response").replace(/\*{2,}/g, "").replace(/#{1,}/g, "").replace(/^\* (.+)$/gm, "• $1");
            setChatMessages(prev => [...prev, { role: "bot", text: formatAIResponse(answer) }]);
            setLastBotText(stripHtml(answer));
        } catch (err) { console.error("Chat error", err); }
        finally { setIsChatLoading(false); }
    };

    // ── Navigation handlers (unchanged) ───────────────────────────────────────
    const handleCreateCourseStep2 = async () => {
        const mergedContent = `${transcribedTxt || ""}\n\n${scrappedContent || ""}`;
        stopAllAudio();
        //navigate("/codegenie/new-course", { state: { FromSignUp: FromSignUp === "true" ? FromSignUp : "", transcribedText: mergedContent } });
        navigate('/codegenie/new-course-4', { state: { FromSignUp: FromSignUp === 'true' ? FromSignUp : '' } });
    };

    const handleCreateCourseStepPrevious = async () => {
        if (FromDT === "true") {
            stopAllAudio(); localStorage.setItem("activeItem", "digital"); navigate("/codegenie/generated-blogs");
        } else {
            stopAllAudio();
            navigate("/codegenie/new-course-1", { state: { FromSignUp: FromSignUp === "true" ? FromSignUp : "" } });
        }
    };

    const handleItemClick = (itemName: any) => {
        localStorage.setItem("activeItem", itemName);
        if (itemName === "logout") {
            dispatch(logout()); localStorage.clear(); removeCookie("user_email"); toast.success("Logout success"); redirectAfterLogout(); //"https://ocesha.ai";
        } else if (itemName === "course") { navigate("/codegenie/generated-courses"); }
        else { navigate("/codegenie/generated-blogs"); }
    };

    // ── Step indicator config ─────────────────────────────────────────────────
    const steps = [
          //"About You",
          "Add Content",
          //"Your Expertise",
          "Preferences",
        ];

    const activeStep = 0;

    useEffect(() => {
        console.log({
            isPlaying,
            isMuted,
            currentAudioType,
        });
    }, [isPlaying, isMuted, currentAudioType]);

    const isSpeakerAnimating = (currentAudioType !== null || isPlaying) && !isMuted;  

    // ── Build display tasks from processingStatus ─────────────────────────────
    const displayTasks = useMemo(() => {
        const tasks: Array<{
            type: string;
            progress: number;
            status: TaskStatus;
            items: ProcessingItem[];
        }> = [];

        // Real tasks from processingStatus (audio, video, web_urls, youtube_urls)
        if (processingStatus?.content) {
            Object.entries(processingStatus.content).forEach(([type, section]) => {
                if (type === "documents") return; // handled separately below

                const s = section as ContentProcessingStatus;
                const items = s.items || [];
                if (items.length === 0) return;

                tasks.push({
                    type,
                    progress: getSectionProgress(items),
                    status: getSectionStatus(items),
                    items,
                });
            });
        }

        // Synthetic documents task — driven by the fake 10%-per-second progress
        const docs = (courseMedia as any)?.result?.documents;
        if (Array.isArray(docs) && docs.length > 0) {
            const docStatus: TaskStatus =
                documentProgress >= 100 ? "complete" : "in_progress";

            const items: ProcessingItem[] = docs.map((d: any) => ({
                identifier: d.path,
                name: d.name,
                progress: documentProgress,
                status: docStatus,
            }));

            tasks.push({
                type: "documents",
                progress: documentProgress,
                status: docStatus,
                items,
            });
        }

        // Synthetic text task — driven by the fake 10%-per-second progress
        const textContent = (courseMedia as any)?.result?.text_documents;
        const hasText = Array.isArray(textContent)
            ? textContent.length > 0
            : !!textContent;

        if (hasText) {
            const textStatus: TaskStatus =
                textProgress >= 100 ? "complete" : "in_progress";

            const textItems: ProcessingItem[] = Array.isArray(textContent)
                ? textContent.map((t: any, i: number) => ({
                      identifier: t.identifier || t.path || `text-${i}`,
                      name: t.name || `Text content ${i + 1}`,
                      progress: textProgress,
                      status: textStatus,
                  }))
                : [
                      {
                          identifier: "text-1",
                          name: "Your text content",
                          progress: textProgress,
                          status: textStatus,
                      },
                  ];

            tasks.push({
                type: "text_documents",
                progress: textProgress,
                status: textStatus,
                items: textItems,
            });
        }

        // Display order matching the design template
        const order = ["documents", "text_documents", "web_urls", "youtube_urls", "audio", "video"];
        tasks.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));

        return tasks;
    }, [processingStatus, courseMedia, documentProgress, textProgress]);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                @keyframes fadeSlideUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
                @keyframes pulseDot    { 0%,100%{opacity:1} 50%{opacity:0.4} }
            `}</style>

            <div style={{ overflowX: "hidden" }}>
                <div className="row">
                    <div className={FromSignUp === "true" ? "col-sm-12 col-md-12 col-lg-12" : "col-sm-12 col-md-10 col-lg-10"}>

                        {/* Header omitted in original — kept same */}
                        {FromSignUp === "true" ? <Divider sx={{ mt: 2 }} /> : <hr />}

                        <div className="overlay-container" style={{ paddingRight: FromSignUp === "true" ? "10%" : undefined, paddingLeft: FromSignUp === "true" ? "10%" : undefined }}>
                            <div style={{ width: "98%", marginBottom: "10%" }}>

                                {/* Upload loading overlay (unchanged) */}
                                {isloadingGetPage && !getPageError && (
                                    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(255,255,255,0.1)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", backdropFilter: "blur(5px)", overflow: "hidden" }}>
                                        <div style={{ width: "80%", maxWidth: "700px", padding: "20px", backgroundColor: "#282C39", borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", textAlign: "center" }}>
                                            <Typography style={{ fontSize: "30px" }}>{loadingText}</Typography><br />
                                            <ProgressBar completed={loaderProgress} bgColor={loaderProgress >= 90 ? "#4CAF50" : "#4BA5EB"} height="20px" labelColor="#ffffff" labelAlignment="center" /><br />
                                            <Typography style={{ fontSize: "20px" }}>Sit tight, your content monster AI Agents have got you covered…</Typography>
                                        </div>
                                    </div>
                                )}

                                <div className="row gx-2">

                                    {/* ════════ LEFT PANEL (unchanged) ════════ */}
                                    <div className="col-sm-12 col-md-4 col-lg-4 py-4" style={{ paddingLeft: "1.5rem" }}>
                                        
                                        <Box
                                            sx={{
                                                position: "relative",
                                                display: "block",
                                                width: "100%",
                                                overflow: "visible",
                                            }}
                                        >
                                            {/* Rohan image — no bump animation */}
                                            <Box
                                                component="img"
                                                src={RohanImg}
                                                alt="Rohan Hall"
                                                sx={{
                                                    position: "relative",
                                                    zIndex: 1,
                                                    width: "100%",
                                                    display: "block",
                                                    objectFit: "cover",
                                                }}
                                            />

                                            {/* Waveform bars — overlaid on the bottom of the image */}
                                            <Box
                                                sx={{
                                                    display: isSpeakerAnimating ? "flex" : "none",
                                                    position: "absolute",
                                                    left: 0,
                                                    right: 0,
                                                    bottom: "16px",          // distance from the bottom edge of the image
                                                    zIndex: 2,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "3px",
                                                    height: "48px",
                                                    px: 2,
                                                    pointerEvents: "none",   // so it doesn't block clicks on the image
                                                    background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.35) 100%)",
                                                }}
                                            >
                                                {[
                                                    4, 10, 18, 8, 22, 14, 26, 6, 16, 30, 12, 20, 8, 24, 10, 18, 4, 14, 22, 8,
                                                    16, 28, 10, 20, 6, 24, 12, 18, 8, 14, 26, 10, 16, 22, 6, 20, 12, 8, 24, 14,
                                                ].map((peak, i) => (
                                                    <Box
                                                        key={i}
                                                        sx={{
                                                            width: "2.5px",
                                                            minWidth: "2.5px",
                                                            borderRadius: "2px",
                                                            background: "#3B82F6",
                                                            animation: `waveStrip${i} 1.1s ease-in-out infinite`,
                                                            animationDelay: `${(i % 10) * 0.08}s`,
                                                            [`@keyframes waveStrip${i}`]: {
                                                                "0%, 100%": { height: "6px" },
                                                                "50%": { height: `${peak}px` },
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        <Paper sx={{ background: "linear-gradient(180deg, rgba(11,12,20,1) 0%, rgba(10,14,28,1) 100%)", borderTopLeftRadius: 0, borderTopRightRadius: 0, borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px", border: "1px solid rgba(255,255,255,0.08)", p: 3, mb: 2 }}>
                                            <Box position="relative"
                                                sx={{
                                                    maxHeight: "300px", // fixed height
                                                    overflowY: "auto",
                                                    pr: 1, // space for scrollbar

                                                    // Thin scrollbar
                                                    "&::-webkit-scrollbar": {
                                                      width: "4px",
                                                    },
                                                    "&::-webkit-scrollbar-track": {
                                                      background: "transparent",
                                                    },
                                                    "&::-webkit-scrollbar-thumb": {
                                                      background: "rgba(255,255,255,0.18)",
                                                      borderRadius: "10px",
                                                    },
                                                    "&::-webkit-scrollbar-thumb:hover": {
                                                      background: "rgba(255,255,255,0.3)",
                                                    },

                                                    // Firefox
                                                    scrollbarWidth: "thin",
                                                    scrollbarColor: "rgba(255,255,255,0.18) transparent",
                                                  }}>
                                                <Typography sx={{ fontSize: "11px", letterSpacing: "0.08em", color: "#4BA5EB", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                                    <AutoAwesomeIcon sx={{ fontSize: 18 }} /> MEET YOUR AI CONCIERGE
                                                </Typography>
                                                <Typography sx={{ color: "#fff", fontSize: "22px", fontWeight: 600, mb: 1.5 }}>
                                                    Hi {userDetails?.first_name},
                                                </Typography>
                                                <Typography sx={{ color: "#A0AEC0", fontSize: "14px", lineHeight: "22px", mb: 2 }}>
                                                    We're analyzing your content to understand your expertise, experience, and teaching style.
                                                </Typography>

                                                {/* "This helps us create for you" list */}
                                                <Typography sx={{ color: "#A0AEC0", fontSize: "14px", lineHeight: "22px", mb: 2 }}>
                                                    This helps OceSha create courses that reflect your knowledge and prepares your AI to teach and answer student questions.
                                                </Typography>
                                                <br/>
                                                {/*<Typography sx={{ color: "#A0AEC0", fontSize: "14px", lineHeight: "22px", mb: 2 }}>
                                                    Up next: a short AI interview to learn more about your expertise.
                                                </Typography>*/}
                                            </Box>

                                           
                                            <Box sx={{ mt: 3, mb: 2, height: "1px", background: "rgba(255,255,255,0.08)" }} />
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                <Typography sx={{ fontSize: "13px", color: "#D1D5DB" }}>Onboarding Progress</Typography>
                                                <Typography sx={{ fontSize: "13px", color: "#A0AEC0" }}>{activeStep + 1} of {steps.length}</Typography>
                                            </Stack>
                                            <Box sx={{ width: "100%", height: "6px", borderRadius: "10px", background: "rgba(255,255,255,0.1)", overflow: "hidden" }}>
                                                <Box sx={{ width: `${((activeStep + 1) / steps.length) * 100}%`, height: "100%", borderRadius: "10px", background: "linear-gradient(90deg,#4BA5EB,#6C63FF)", transition: "width 0.3s ease" }} />
                                            </Box>
                                        </Paper>

                                        {/* Ask AI box */}
                                        <Paper sx={{ p: 2, background: "#2b2b32", borderRadius: "8px" }}>
                                            <Box
                                                ref={chatScrollRef}
                                                sx={{ maxHeight: 200, overflowY: "auto", mb: 1, scrollBehavior: "smooth" }}
                                                >
                                                {chatMessages.map((msg, i) => (
                                                    <Box key={i} sx={{ textAlign: msg.role === "user" ? "right" : "left", mb: 1 }}>
                                                        <Typography sx={{ display: "inline-block", px: 1.5, py: 0.8, borderRadius: 2, background: msg.role === "user" ? "#4BA5EB" : "#000000", color: "#fff", fontSize: "14px" }}>
                                                            {msg.role === "bot" ? <Box dangerouslySetInnerHTML={{ __html: msg.text }} /> : msg.text}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {isChatLoading && <Typography sx={{ color: "#aaa", fontSize: 12 }}>Thinking...</Typography>}
                                            </Box>
                                            <TextareaAutosize value={questionText} onChange={e => setQuestionText(e.target.value)} placeholder="Ask AI anything..." minRows={2}
                                                style={{ width: "100%", background: "#2b2b32", color: "#fff", border: "none", outline: "none" }} />
                                            <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
                                                <IconButton onClick={toggleMute} sx={{ background: isMuted ? "linear-gradient(135deg,#667eea,#764ba2)" : "linear-gradient(135deg,#4BA5EB,#1E7BC0)", borderRadius: "12px", width: 45, height: 45, border: "1px solid rgba(255,255,255,0.2)", "&:hover": { transform: "scale(1.1)" }, animation: !isMuted ? "speakerPulse 2s infinite" : "none", "@keyframes speakerPulse": { "0%": { boxShadow: "0 4px 15px rgba(75,165,235,0.5)" }, "50%": { boxShadow: "0 4px 25px rgba(75,165,235,0.9)" }, "100%": { boxShadow: "0 4px 15px rgba(75,165,235,0.5)" } } }}>
                                                    {isMuted ? <VolumeOffIcon sx={{ color: "#fff", fontSize: "22px" }} /> : <VolumeUpIcon sx={{ color: "#fff", fontSize: "22px" }} />}
                                                </IconButton>
                                                <IconButton onClick={() => isRecording ? stopRecording() : startRecording()} className={isRecording ? "recording-mic-btn" : "mic-btn"}>
                                                    <MicNoneOutlinedIcon sx={{ color: "#fff", fontSize: "20px" }} />
                                                </IconButton>
                                                <IconButton onClick={() => getChatBotResponse(questionText)}>
                                                    <SendIcon sx={{ color: "#fff" }} />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    </div>

                                    {/* ════════ RIGHT PANEL — NEW DESIGN ════════ */}
                                    <div className="col-md-8">
                                        <Box sx={{ px: { xs: 2, md: 4 }, pl: { md: 2 }, py: 2 }}>

                                            {/* Back button */}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", mb: 2, width: "fit-content" }} onClick={() => navigate(-1)}>
                                                <ArrowBackIcon sx={{ fontSize: 20 }} />
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Back</Typography>
                                            </Box>

                                            {/* Step indicator */}
                                            <Box sx={{ maxWidth: "500px", mx: "auto", px: 2 }}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mb: 4 }}>
                                                    {steps.map((label, index) => {
                                                        const isActive    = index === activeStep;
                                                        const isCompleted = index < activeStep;
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <Stack alignItems="center" spacing={1}>
                                                                    <Box sx={{ width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: 600, color: "#fff", background: isActive || isCompleted ? "linear-gradient(90deg,#4BA5EB,#6C63FF)" : "transparent", border: isActive || isCompleted ? "none" : "1px solid rgba(255,255,255,0.2)" }}>
                                                                        {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
                                                                    </Box>
                                                                    <Typography sx={{ fontSize: "12px", color: isActive || isCompleted ? "#fff" : "#A0AEC0" }}>{label}</Typography>
                                                                </Stack>
                                                                {index !== steps.length - 1 && (
                                                                    <Box sx={{ flex: 1, height: "1px", mx: 0, mt: "-18px", background: index < activeStep - 1 ? "repeating-linear-gradient(to right,#4BA5EB 0 3px,transparent 3px 6px)" : "repeating-linear-gradient(to right,rgba(255,255,255,0.3) 0 3px,transparent 3px 6px)" }} />
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>

                                            {/* ── Heading ── */}
                                            <Typography sx={{ fontSize: { xs: "22px", md: "32px" }, fontWeight: 700, color: "#fff", lineHeight: 1.3, mb: 1.5, textAlign: "center", }}>
                                                We're Analyzing Your Content{" "}
                                            </Typography>
                                            <Typography sx={{ fontSize: "14px", color: "#A0AEC0",  lineHeight: 1.6, mb: 4, textAlign: "center", }}>
                                                We're analyzing your content to understand your expertise, knowledge, and teaching style so we can create better courses from your experience.
                                            </Typography>

                                            {/* ── "Up Next" card ── */}
                                            {/*<Box sx={{ borderRadius: "16px", mb: 3, background: "linear-gradient(135deg,rgba(79,70,229,0.25),rgba(109,40,217,0.18))", border: "1px solid rgba(109,40,217,0.35)", p: "18px 22px", display: "flex", alignItems: "center", gap: 2.5 , width: "90%", mx: "auto"}}>
                                                <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: "linear-gradient(135deg,rgba(109,40,217,0.6),rgba(79,70,229,0.8))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 0 20px rgba(109,40,217,0.4)" }}>
                                                    <MicRoundedIcon sx={{ color: "#c084fc", fontSize: 26 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#e8eaf0", mb: 0.6 }}>
                                                        Up Next: Your Personalized AI Interview
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.6 }}>
                                                        Your AI Concierge will conduct a short, personalized interview to fill in the gaps and learn more about your expertise.
                                                    </Typography>
                                                </Box>
                                            </Box>*/}

                                            {/* ── Processing task cards (LIVE from processingStatus) ── */}
                                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                                                {displayTasks.length > 0 ? (
                                                    displayTasks.map(task => (
                                                        <TaskCard key={task.type} type={task.type} progress={task.progress} status={task.status} items={task.items} />
                                                    ))
                                                ) : (
                                                    /* skeleton while data loads */
                                                    ["audio", "documents", "web_urls"].map(key => (
                                                        <Box key={key} sx={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px", p: "16px 20px", display: "flex", alignItems: "center", gap: 2 }}>
                                                            <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "rgba(255,255,255,0.06)", animation: "pulseDot 1.5s ease-in-out infinite" }} />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Box sx={{ height: 14, width: "40%", borderRadius: 99, background: "rgba(255,255,255,0.08)", mb: 1, animation: "pulseDot 1.5s ease-in-out infinite" }} />
                                                                <Box sx={{ height: 5, width: "100%", borderRadius: 99, background: "rgba(255,255,255,0.06)", animation: "pulseDot 1.5s ease-in-out infinite" }} />
                                                            </Box>
                                                        </Box>
                                                    ))
                                                )}
                                            </Stack>

                                            {/* ── "Preparing AI Interview" footer card ── */}
                                            {/*
                                            <Box sx={{
                                                borderRadius: "14px",
                                                background: "rgba(255,255,255,0.025)",
                                                border: `1px solid ${
                                                    interviewReady ? "rgba(34,197,94,0.25)"
                                                    : interviewError ? "rgba(239,68,68,0.25)"
                                                    : "rgba(255,255,255,0.07)"
                                                }`,
                                                p: "16px 20px",
                                                display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2,
                                            }}>
                                                <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ flex: 1 }}>
                                                    <Box sx={{
                                                        width: 44, height: 44, borderRadius: "12px",
                                                        background: interviewReady ? "rgba(34,197,94,0.12)" : "rgba(75,165,235,0.12)",
                                                        border: `1px solid ${interviewReady ? "rgba(34,197,94,0.25)" : "rgba(75,165,235,0.25)"}`,
                                                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                    }}>
                                                        {interviewReady
                                                            ? <CheckIcon sx={{ color: "#22c55e", fontSize: 22 }} />
                                                            : isPreparingInterview
                                                                ? <CircularProgress size={20} sx={{ color: "#4BA5EB" }} />
                                                                : <AccessTimeIcon sx={{ color: "#4BA5EB", fontSize: 22 }} />}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#e8eaf0", mb: 0.5 }}>
                                                            {interviewReady
                                                                ? "Your AI Interview is Ready"
                                                                : "Preparing Your Personalized AI Interview"}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 13, color: "#7a7d96", lineHeight: 1.55 }}>
                                                            {interviewReady
                                                                ? "We've built personalized interview questions based on your content. Click Continue to begin."
                                                                : interviewError
                                                                    ? "Something went wrong preparing your interview. Try refreshing or continue — we'll retry."
                                                                    : isPreparingInterview
                                                                        ? "Your AI Concierge is building interview questions based on your uploaded content…"
                                                                        : "Your AI Concierge will build interview questions once content analysis completes."}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Box sx={{
                                                    display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
                                                    flexShrink: 0, minWidth: 90, borderLeft: "1px solid rgba(255,255,255,0.07)", pl: 2.5,
                                                }}>
                                                    {interviewReady
                                                        ? <CheckIcon sx={{ color: "#22c55e", fontSize: 20 }} />
                                                        : <AccessTimeIcon sx={{ color: "#6b7280", fontSize: 20 }} />}
                                                    <Typography sx={{ fontSize: 12, color: "#7a7d96", textAlign: "center", lineHeight: 1.4 }}>
                                                        {interviewReady ? "Ready" : <>This usually takes<br />about 1 minute.</>}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            */}

                                        </Box>

                                        {/* Nav buttons — inside right panel */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            spacing={1.5}
                                            sx={{ mt: 4, pb: 2 }}
                                        >
                                            <Button
                                                variant="outlined"
                                                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => {
                                                    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
                                                    handleCreateCourseStepPrevious();
                                                }}
                                                sx={{
                                                    height: "42px", px: 3, borderRadius: "8px",
                                                    textTransform: "none", fontWeight: 500, fontSize: "14px",
                                                    color: "#D1D5DB", borderColor: "rgba(255,255,255,0.12)",
                                                    "&:hover": { borderColor: "#3B82F6", background: "rgba(59,130,246,0.06)" },
                                                }}
                                            >
                                                Previous
                                            </Button>

                                            <Button
                                                variant="contained"
                                                disabled={!canContinue}
                                                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => {
                                                    if (FromDT === "true") { setOpenConfirm(true); }
                                                    else {
                                                        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
                                                        handleCreateCourseStep2();
                                                    }
                                                }}
                                                sx={{
                                                    height: "42px", px: 3, borderRadius: "8px",
                                                    textTransform: "none", fontWeight: 600, fontSize: "14px",
                                                    color: "#fff", minWidth: "140px",
                                                    background: canContinue
                                                        ? "linear-gradient(90deg,#3B82F6,#2563EB)"
                                                        : "rgba(255,255,255,0.1)",
                                                    boxShadow: canContinue ? "0 4px 18px rgba(59,130,246,0.35)" : "none",
                                                    "&:hover": {
                                                        background: canContinue ? "linear-gradient(90deg,#2563EB,#1D4ED8)" : undefined,
                                                    },
                                                    "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
                                                }}
                                            >
                                                {isUpdateLoader ? <CircularProgress size={24} style={{ color: "#fff" }} /> : "Continue"}
                                            </Button>
                                        </Stack>

                                    </div>
                                    {/* end col-md-8 */}

                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Confirm dialog (unchanged) ── */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Course Creation</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to create this course? Once created, you can proceed to the next step.</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: "16px" }}>
                    <MuiButton variant="outlined" color="secondary"
                        onClick={() => {
                            if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
                            localStorage.setItem("activeItem", "blogs"); navigate("/codegenie/generated-blogs");
                        }}>
                        No
                    </MuiButton>
                    <MuiButton variant="contained" color="primary"
                        onClick={() => {
                            setOpenConfirm(false);
                            if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
                            handleCreateCourseStep2();
                        }}>
                        Yes, Create
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* Upgrade modal (unchanged) */}
            <Modal open={upgradeModal} disableEscapeKeyDown>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: isMobile ? "95%" : "40%", bgcolor: "background.paper", border: "2px solid #000", boxShadow: 24, pt: 2, px: 4, pb: 3, borderRadius: "15px", padding: "3%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Typography style={{ textAlign: "center", fontWeight: "bold" }}><InfoIcon sx={{ color: "#ffcc00" }} /><br />This feature is not included in your plan. Please upgrade to have access to this feature.</Typography>
                    <Button onClick={() => setUpgradeModal(false)} variant="contained" sx={{ mt: 2 }}>Close</Button>
                </Box>
            </Modal>
        </>
    );
};

export default New_Course_2;